import React, { useContext } from "react";
import _ from 'lodash'

import { useFilter } from "./Filter";
import {
    FilteredDesigns,
    countries_filter,
    filterDesignsByCountry,
    reference_filter,
    filterDesignsByReference,
    free_text_filter,
    filterDesignsByText,
    owner_filter,
    contact_filter,
    filterDesignsByAgent,
    designer_filter,
    filterByDesignStatus,
} from "./utils";
import { FilterConfig } from "./utils";
import { useDesigns } from "../designs/DesignsProvider";
import { DesignFamily, DesignRegistration } from "src/designs/designs";
import { translateForDesigns } from "./IpStatusFilter";

const FilteredDesignsContext = React.createContext({
    designFamilies: [] as DesignFamily[],
    designRegistrations: [] as DesignRegistration[],

    familyById: {} as Record<number, DesignFamily>,
    registrationsByFamilyId: {} as Record<number, DesignRegistration[]>,
    registrationById: {} as Record<number, DesignRegistration>,
})

export function FilteredDesignsProvider({ children }: { children: React.ReactNode }) {
    const { /*showActiveOnly,*/ activeFilters, ipStates } = useFilter()

    const { families, registrations, registrationsByFamilyId: allRegistrationsByFamilyId, designersByRegistrationId, ownersByRegistrationId, contactsByRegistrationId } = useDesigns()

    // Build agentsLookup similar to trademarks - maps role -> agentId -> registrationIds
    const agentsLookup = React.useMemo(() => {
        const lookup: Record<string, Record<number, number[]>> = {
            [designer_filter]: {},
            [owner_filter]: {},
            [contact_filter]: {},
        }

        // Invert the lookups to go from agentId to registrationIds
        Object.entries({
            [designer_filter]: designersByRegistrationId,
            [owner_filter]: ownersByRegistrationId,
            [contact_filter]: contactsByRegistrationId,
        }).forEach(([role, byRegistrationId]) => {
            Object.entries(byRegistrationId).forEach(([regIdStr, agents]) => {
                const regId = parseInt(regIdStr)
                agents.forEach(agent => {
                    if (!lookup[role][agent.agentId]) {
                        lookup[role][agent.agentId] = []
                    }
                    lookup[role][agent.agentId].push(regId)
                })
            })
        })

        return lookup
    }, [designersByRegistrationId, ownersByRegistrationId, contactsByRegistrationId])

    // Build agentsByRegistrationId similar to trademarks - maps role -> registrationId -> agentIds
    const agentsByRegistrationId = React.useMemo(() => {
        const lookup: Record<string, Record<number, number[]>> = {
            [designer_filter]: {},
            [owner_filter]: {},
            [contact_filter]: {},
        }

        Object.entries({
            [designer_filter]: designersByRegistrationId,
            [owner_filter]: ownersByRegistrationId,
            [contact_filter]: contactsByRegistrationId,
        }).forEach(([role, byRegistrationId]) => {
            Object.entries(byRegistrationId).forEach(([regIdStr, agents]) => {
                const regId = parseInt(regIdStr)
                lookup[role][regId] = agents.map(a => a.agentId)
            })
        })

        return lookup
    }, [designersByRegistrationId, ownersByRegistrationId, contactsByRegistrationId])

    const fullContext = activeFilters
        .filter(({ isActive }) => isActive)
        .reduce(
            (ctxt: FilteredDesigns, filter: FilterConfig) => {
                switch (filter.name) {
                    case countries_filter:
                        return filterDesignsByCountry(ctxt, filter.config);
                    case reference_filter:
                        return filterDesignsByReference(ctxt, filter.config);
                    case free_text_filter:
                        return filterDesignsByText(ctxt, filter.config);
                    // case designer_filter:
                    case owner_filter:
                    //case contact_filter:
                        return filterDesignsByAgent(ctxt, filter.config, filter.name, agentsLookup, agentsByRegistrationId);
                    default:
                        console.warn(`Unknown filter type: ${filter.name}`)
                        return ctxt;
                }
            },
            { designFamilies: families, designRegistrations: registrations }
        );

    const context = filterByDesignStatus(fullContext, ipStates.flatMap(translateForDesigns), allRegistrationsByFamilyId)

    const value = {
        ...context,
        familyById: _.keyBy(context.designFamilies, f => f.designFamilyId),
        registrationsByFamilyId: _.groupBy(context.designRegistrations, r => r.familyId),
        registrationById: _.keyBy(context.designRegistrations, r => r.registrationId),
    }

    return (
        <FilteredDesignsContext.Provider value={value}>{children}</FilteredDesignsContext.Provider>
    );
}

export function useFilteredDesigns() {
    return useContext(FilteredDesignsContext);
}

export function useAllOrFilteredDesigns(applyGlobalFilter = true) {
    const allContext = useDesigns()
    const filteredContext = useFilteredDesigns()

    return applyGlobalFilter ? filteredContext : allContext
}
